/**
 * Model for institution dropdown: institution code and name.
 */
export class InstitutionDropdownModel { instCd!: string; instName!: string; }

/**
 * Model for DDP code dropdown: code and display value.
 */
export class DdpCodeDropdownModel { ddpCd!: string; displayCd!: string; }

/**
 * Model for DPP code dropdown: code and display value.
 */
export class DppCodeDropdownModel { dppCd!: string; displayCd!: string; }

/**
 * Model for designation dropdown: type code and designation.
 */
export class DesignationDropdownModel { designationTyc!: string; designation!: string; }

/**
 * Model for hearing type dropdown: type code and name.
 */
export class HearingTypeDropdownModel { hearingTyc!: string; hearingName!: string; }

/**
 * Model for county dropdown: county code and name.
 */
export class CountyDropdownModel { ctyCd!: string; ctyName!: string; }

/**
 * Model for county dropdown by ID: county number, code, and name.
 */
export class CountyByIdDropdownModel { ctyNo!: number; ctyCd!: string; ctyName!: string; }

/**
 * Model for facility dropdown: institution code, facility code, and name.
 */
export class FacilityDropdownModel { instCd!: string; facCd!: string; facName!: string; }

/**
 * Model for participant title dropdown: code and name.
 */
export class ParticipantTitleDropdownModel { code!: string; name!: string; }

/**
 * Model for MEDL agency type dropdown: ID and name.
 */
export class MedlAgencyTypeDropdownModel { id!: number; name!: string; }

/**
 * Model for MEDL county dropdown: county code and name.
 */
export class MedlCountyDropdownModel { ctyCd!: string; ctyName!: string; }
import { inject } from "@angular/core";

// EZ
import { InputSelectOption } from "@template-ui/types/Input-select-option";
import { EZDropdown } from "@template-ui/types";
import { read } from "@template-ui/helpers/signal.helpers";

// BITS
import { ParticipantSelectListItem } from "./participant-select-item.types";
import { AppApis } from "@app/core/APIs/app-apis";
import { PhmsIntakeRuntimeService } from "@app/phms/intake/phms-intake-runtime.service";


/**
 * Defines a reusable factory function that returns a `DropdownConfig` instance.
 *
 * This is helpful for encapsulating configuration logic and maintaining consistency.
 *
 * @template T - The type of items used in the dropdown.
 * @param factory - A function that returns a `DropdownConfig` object.
 * @returns The same factory function passed in, for use elsewhere (e.g. registration or consumption).
 *
 * @example
 * const userDropdown = defineDropdownConfig(() => ({
 *   endpoint: '/api/users',
 *   getValue: user => user.id,
 *   getLabel: user => user.name
 * }));
 */
export function defineDropdownConfig<T>(factory: () => EZDropdown<T>): () => EZDropdown<T> {
    return factory;
}

/**
 * Creates a refined `DropdownConfig` from an existing one, using filtering and optional overrides.
 *
 * Useful for generating context-specific dropdowns based on a shared base config,
 * such as scoping a user list to active users only.
 *
 * @template T - The type of items used in the dropdown.
 * @param baseConfig - The original dropdown configuration to derive from.
 * @param filterFn - A predicate function used to filter items either statically or post-fetch.
 * @param overrides - Optional configuration overrides to apply to the derived config.
 * @returns A new `DropdownConfig` object representing the filtered subset.
 *
 * Notes:
 * - If `staticItems` are present in `baseConfig`, filtering is applied immediately and `filterAfterFetch` is disabled.
 * - If no `staticItems` are present, `filterAfterFetch` is assigned to ensure filtering occurs after fetch.
 *
 * @example
 * const activeUserConfig = createDropdownSubsetConfig(
 *   userDropdownConfig,
 *   user => user.isActive,
 *   { labelTemplate: '{name} - Active' }
 * );
 */
export function createDropdownSubsetConfig<T>(
    baseConfig: EZDropdown<T>,
    filterFn: (item: T) => boolean,
    overrides?: Partial<EZDropdown<T>>
): EZDropdown<T> {
    // Handle both array and Signal for staticItems
    const items = baseConfig.staticItems ? read(baseConfig.staticItems) : undefined;
    const subset = items?.filter(filterFn);

    const config: EZDropdown<T> = {
        name: overrides?.name ?? baseConfig.name,
        getValue: overrides?.getValue ?? baseConfig.getValue,
        getLabel: overrides?.getLabel ?? baseConfig.getLabel,
        labelTemplate: overrides?.labelTemplate ?? baseConfig.labelTemplate,
        endpointVerbType: overrides?.endpointVerbType ?? baseConfig.endpointVerbType,
        body: overrides?.body ?? baseConfig.body,
        endpoint: overrides?.endpoint ?? baseConfig.endpoint,
        filterAfterFetch: baseConfig.staticItems ? undefined : filterFn
    };

    if (subset) {
        config.staticItems = subset;
    }

    return config;
}

// export type ParentValues = Record<string, unknown>;


// /**
//  * Configuration contract for retrieving and formatting dropdown options from an API source.
//  *
//  * @template T - The shape of the data items returned by the API.
//  *
//  * Provides flexible hooks for value extraction, label formatting, and API method control.
//  */
// export interface EZDropdown<T = any> {
//   name: string;
//   /**
//    * The full URL or endpoint for fetching dropdown data.
//    * Can be dynamically generated if needed.
//    */
//   endpoint?: string;

//   /**
//    * The HTTP method to use when calling the endpoint.
//    * Defaults to `'POST'` if omitted.
//    */
//   endpointVerbType?: 'GET' | 'POST';

//   /**
//    * Optional request body to send with the API call.
//    * Typically used with POST endpoints.
//    */
//   body?: any;

//   /**
//    * A function to extract the value from each item (used as the option's internal value).
//    *
//    * Example: `(item) => item.id`
//    */
//   getValue: (item: T) => string | number;

//   /**
//    * A function to generate the display label for each item.
//    * Used if `labelFormatter` is not provided.
//    *
//    * Example: `(item) => item.name`
//    */
//   getLabel?: (item: T) => string;

//   /**
//    * Optional template string used to format labels if `getLabel` is not provided.
//    * Supports placeholder substitution using item properties.
//    *
//    * Example: `'User: {{name}} ({{id}})'`
//    */
//   labelTemplate?: string;

//   /**
//    * Static array of dropdown items.
//    * Use this instead of `endpoint` and `body` when options are locally available.
//    */
//   staticItems?: T[];

//   /**
//    * Indicates a dependency on another dropdown.
//    * Used for building cascaded dropdown behavior.
//    * May reference a key or another config object.
//    */
//   dependsOn?: EZDropdown<any>;

//   /**
//    * Function that filters options based on the value of the parent dropdown.
//    * Applied after options are retrieved but before rendering.
//    *
//    * Example: `(parentValue, items) => items.filter(item => item.categoryId === parentValue)`
//    */
//   filterOnParentValue?: (parentValue: any, allItems: T[]) => T[];

//    // NEW, multi-parent API (backwards-compatible)
//   dependsOnMany?: EZDropdown<any>[];                 // array of parent dropdown configs
//   filterOnParents?: (parents: ParentValues, all: T[]) => T[];

//   // Experimental: custom cache key generation based on parent values
//   parentCacheKey?: (parents: ParentValues) => string;

//   transformOnParents?: (
//     parents: Map<EZDropdown<any>, any>,
//     items: T[]
//   ) => T[];

//   /**
//    * Optional constructor used to instantiate the data model for dropdown items.
//    * Useful when you need to apply class-based transformation logic after fetch.
//    */
//   model?: new () => T;

//   /**
//    * Optional post-fetch filter applied to individual items.
//    * Allows exclusion of specific entries based on custom logic.
//    *
//    * Example: `(item) => item.isActive`
//    */
//   filterAfterFetch?: (item: T) => boolean;

//   includeAllOption?: boolean;

//   allOptionValue?: any;

//   allOptionLabel?: string;

//   includeClearOption?:boolean;

//   clearOptionLabel?:string;

//   cacheDropdown?: boolean;
// }

/**
 * A map of dropdown configuration factory functions indexed by their unique registry keys.
 *
 * Each factory returns a fully constructed DropdownConfig object, and is evaluated at runtime
 * to allow dependency injection (e.g. using `inject()` inside config functions).
 */
export const commonDropdownConfigFactories = {
    /**
     * Dropdown config for selecting Participants.
     *
     * @remarks
     * This config will POST a list of role-codes to the API, and render each
     * ParticipantSelectListItem by `partId` → `fullName`.  You can patch _any_
     * part of this at the use-site via `dropdownOverride` (in forms) or
     * `cellRendererParams.dropdownOverride` (in AG-Grid).
     *
     * @example
     * // 1) Default behavior (no override):
     * { dropdownKey: DropdownKeys.participants }
     *
     * @example
     * // 2) Override only the roles array:
     * {
     *   dropdownKey: DropdownKeys.participants,
     *   dropdownOverride: {
     *     body: ['ADMIN','QA']
     *   }
     * }
     *
     * @example
     * // 3) Override only the label format:
     * {
     *   dropdownKey: DropdownKeys.participants,
     *   dropdownOverride: {
     *     labelTemplate: '{{lastName}}, {{firstName}}'
     *   }
     * }
     *
     * @example
     * // 4) Override both roles and label:
     * {
     *   dropdownKey: DropdownKeys.participants,
     *   dropdownOverride: {
     *     body: ['SUPPORT'],
     *     labelTemplate: '{{firstName}} ({{email}})'
     *   }
     * }
     */
    participants: defineDropdownConfig<ParticipantSelectListItem>(() => ({
        name: 'participants',
        endpoint: inject(AppApis).core.postFiltersParticipantByRoleByExcludeDevs(false),
        endpointVerbType: 'POST',
        getValue: (p: { partId: any; }) => p.partId,
        labelTemplate: "{{fullName}}"
    })),

    participantsExcludeDevs: defineDropdownConfig<ParticipantSelectListItem>(() => ({
        name: 'participants',
        endpoint: inject(AppApis).core.postFiltersParticipantByRoleByExcludeDevs(true),
        endpointVerbType: 'POST',
        getValue: (p: { partId: any; }) => p.partId,
        labelTemplate: "{{fullName}}"
    })),

    activeAttorney: defineDropdownConfig<ParticipantSelectListItem>(() => ({
        name: 'participants',
        endpoint: inject(AppApis).core.manual.activeAttorneyByRoleByExcludeDevs(true),
        endpointVerbType: 'POST',
        getValue: (p: { partId: any; }) => p.partId,
        labelTemplate: "{{fullName}}"
    })),

    /**
     * Static Yes/No list
     */
    yesNo: defineDropdownConfig(() => ({
        name: 'yesNo',
        staticItems: [
            { value: "Y", label: 'Yes' },
            { value: "N", label: 'No' }
        ],
        getValue: (i: { value: any; }) => i.value,
        getLabel: (i: { label: any; }) => i.label
    })),
    /**
     * A list of all Sentences Types.
     */
    allSentenceTypes: defineDropdownConfig(() => ({
        name: 'allSentenceTypes',
        //TODO: these tags should be added to the response from the API
        staticItems: [
            { value: "ISL", viewValue: 'ISL', tags: ['ALL', 'ISL_DSL', 'NO_COND'] },
            { value: "DSL", viewValue: 'DSL', tags: ['ALL', 'ISL_DSL', 'NO_COND'] },
            { value: "LWOP", viewValue: 'LWOP', tags: ['ALL', 'NO_COND'] },
            { value: "JLWOP", viewValue: 'JLWOP', tags: ['ALL', 'NO_COND'] },
            { value: "COND", viewValue: 'COND', tags: ['ALL'] },

        ] as InputSelectOption[],
        getValue: (i: { value: any; }) => i.value,
        getLabel: (i: { viewValue: any; }) => i.viewValue
    })),
    /**
     * Only ISL and DSL sentence types.
     */
    sentenceTypes_IslDsl: defineDropdownConfig((): EZDropdown<InputSelectOption> =>
        createDropdownSubsetConfig(
            commonDropdownConfigFactories.allSentenceTypes(),
            (item: any) => Array.isArray(item.tags) && item.tags.includes('ISL_DSL'),
            {
                name: "sentenceTypes_IslDsl"
            }
        )
    ),
    /**
     * Institution names mapped by code
     */
    institutions: defineDropdownConfig(() => ({
        name: "institutions",
        /**
         * Model for institution dropdown: institution code and name.
         */
        model: InstitutionDropdownModel,
        endpoint: inject(AppApis).core.getFiltersGetInstitutions(),
        endpointVerbType: 'GET',
        getValue: (i: { instCd: any; }) => i.instCd,
        labelTemplate: "{{instName}}"
    })),
    /**
     * Institution names mapped by code, No BPH Headquarters
     */
    institutionsNoBph: defineDropdownConfig((): EZDropdown<{ instCd: string; instName: string }> =>
        createDropdownSubsetConfig(
            commonDropdownConfigFactories.institutions(),
            (item: any) => !item.instCd?.includes('BPHHQ'),
            {
                name: "institutionsNoBph"
            }
        )
    ),

    institutionsWithCode: defineDropdownConfig(() => ({
        name: "institutions",
        /**
         * Model for institution dropdown: institution code and name.
         */
        model: InstitutionDropdownModel,
        endpoint: inject(AppApis).core.getFiltersGetInstitutions(),
        endpointVerbType: 'GET',
        getValue: (i: { instCd: any; }) => i.instCd,
        labelTemplate: "({{instCd}}) - {{instName}}"
    })),

    /**
     *
     */
    ddpCodes: defineDropdownConfig(() => ({
        name: "ddpCodes",
        /**
         * Model for DDP code dropdown: code and display value.
         */
        model: DdpCodeDropdownModel,
        endpoint: inject(AppApis).core.getFiltersGetDDPCodes(),
        endpointVerbType: 'GET',
        getValue: (i: { ddpCd: any; }) => i.ddpCd,
        labelTemplate: "{{displayCd}}"
    })),
    /**
     *
     */
    dppCodes: defineDropdownConfig(() => ({
        name: "dppCodes",
        /**
         * Model for DPP code dropdown: code and display value.
         */
        model: DppCodeDropdownModel,
        endpoint: inject(AppApis).core.getFiltersGetDPPCodes(),
        endpointVerbType: 'GET',
        getValue: (i: { dppCd: any; }) => i.dppCd,
        labelTemplate: "{{displayCd}}"
    })),
    /**
     *
     */
    designations: defineDropdownConfig(() => ({
        name: "designations",
        /**
         * Model for designation dropdown: type code and designation.
         */
        model: DesignationDropdownModel,
        endpoint: inject(AppApis).core.getFiltersGetDesignations(),
        endpointVerbType: 'GET',
        getValue: (i: { designationTyc: any; }) => i.designationTyc,
        labelTemplate: "{{designation}}"
    })),
    /**
     *
     */
    hearingTypes: defineDropdownConfig(() => ({
        name: "hearingTypes",
        /**
         * Model for hearing type dropdown: type code and name.
         */
        model: HearingTypeDropdownModel,
        endpoint: inject(AppApis).core.getFiltersGetHearingTypes(),
        endpointVerbType: 'GET',
        getValue: (i: { hearingTyc: any; }) => i.hearingTyc,
        labelTemplate: "{{hearingName}}"
    })),
    /**
     *
     */
    counties: defineDropdownConfig(() => ({
        name: "counties",
        /**
         * Model for county dropdown: county code and name.
         */
        model: CountyDropdownModel,
        endpoint: inject(AppApis).core.getFiltersCounty(),
        endpointVerbType: 'GET',
        getValue: (i: { ctyCd: any; }) => i.ctyCd,
        labelTemplate: "{{ctyName}}"
    })),

    /**
    * County dropdown using numeric ID (ctyNo) as value - for reports
    */
    countiesById: defineDropdownConfig(() => ({
        name: "countiesById",
        /**
         * Model for county dropdown by ID: county number, code, and name.
         */
        model: CountyByIdDropdownModel,
        endpoint: inject(AppApis).core.getFiltersCounty(),
        endpointVerbType: 'GET',
        getValue: (i: { ctyNo: any; }) => i.ctyNo,
        labelTemplate: "{{ctyName}}"
    })),

    /**
     *
     */
    facilities: defineDropdownConfig(() => ({
        name: "facilities",
        /**
         * Model for facility dropdown: institution code, facility code, and name.
         */
        model: FacilityDropdownModel,
        endpoint: inject(AppApis).core.getFiltersGetHearingTypes(),
        endpointVerbType: 'GET',
        getValue: (i: { facCd: any; }) => i.facCd,
        labelTemplate: "{{facName}}"
    })),

    participantTitles: defineDropdownConfig<any>(() => ({
        name: 'participantTitles',
        /**
         * Model for participant title dropdown: code and name.
         */
        model: ParticipantTitleDropdownModel,
        endpoint: inject(AppApis).core.getFiltersGetParticipantTitles(),
        endpointVerbType: 'GET',
        getValue: (p: { code: any; }) => p.code,
        labelTemplate: "{{name}}"
    })),

    /**
     * MEDL Agency Types
     */
    medlAgencyTypes: defineDropdownConfig(() => ({
        name: "medlAgencyTypes",
        /**
         * Model for MEDL agency type dropdown: ID and name.
         */
        model: MedlAgencyTypeDropdownModel,
        endpoint: inject(AppApis).core.getMEDLAgencyType(),
        endpointVerbType: 'GET',
        getValue: (i: { id: any; }) => i.id,
        labelTemplate: "{{name}}",
        cacheDropdown: true
    })),

    /**
     * MEDL Counties
     */
    medlCounties: defineDropdownConfig(() => ({
        name: "medlCounties",
        /**
         * Model for MEDL county dropdown: county code and name.
         */
        model: MedlCountyDropdownModel,
        endpoint: inject(AppApis).core.getMEDLCounties(),
        endpointVerbType: 'GET',
        getValue: (i: { ctyCd: any; }) => i.ctyCd,
        labelTemplate: "{{ctyName}}",
        cacheDropdown: true
    })),

    phmsIntakeProgramScopeKeys: defineDropdownConfig<InputSelectOption>(() => ({
        name: "phmsIntakeProgramScopeKeys",
        staticItems: inject(PhmsIntakeRuntimeService).programScopeKeyOptions,
        getValue: (option: InputSelectOption) => option.value as string,
        getLabel: (option: InputSelectOption) => option.viewValue,
    })),

} as const;

/**
 * Type alias representing all valid dropdown registry keys.
 *
 * This is inferred directly from `commonDropdownConfigFactories` to ensure consistency and reduce manual maintenance.
 */
export type DropdownKey = keyof typeof commonDropdownConfigFactories;

/**
 * Runtime-safe map of dropdown keys for developer-friendly usage.
 *
 * Enables usage like `DropdownKeys.participants` instead of manually typing string literals.
 */
export const DropdownKeys = Object.keys(commonDropdownConfigFactories)
    .reduce((acc, key) => ({ ...acc, [key]: key }), {}) as {
        [K in keyof typeof commonDropdownConfigFactories]: K;
    };
